<script setup lang="ts">
import type { Component } from "vue";
import { removeElement } from "./helpers/element";
import { definePosition } from "./helpers/position";
import Timer from "./helpers/timer";

export interface BaseToastProps {
  message?: string;
  type?: "success" | "danger";
  position?:
    | "top-right"
    | "top"
    | "top-left"
    | "bottom-right"
    | "bottom"
    | "bottom-left";
  duration?: false | number;
  dismissible?: boolean;
  pauseOnHover?: boolean;
  onClose?: Function;
  onClick?: Function;
  customComponent?: Component;
  customComponentProps?: Record<string, any>;
}

const props = withDefaults(defineProps<BaseToastProps>(), {
  duration: 4000,
  position: "top",
  dismissible: true,
  pauseOnHover: true,
  type: "success",
});

const rootEl = ref(null);
const timer = ref<Timer | null>(null);
const isActive = ref(false);
const parentTop = ref<HTMLElement | null>(null);
const parentBottom = ref<HTMLElement | null>(null);
const transition = computed(() => {
  return definePosition(
    props.position,
    { enter: "fadeInDown", leave: "fadeOut" },
    { enter: "fadeInUp", leave: "fadeOut" },
  );
});
const correctParent = computed(() =>
  definePosition(props.position, parentTop.value, parentBottom.value),
);

const hasCustomComponent = computed(() => !!props.customComponent);

function createParents() {
  parentTop.value = document.querySelector(".BaseToast-container--top") as HTMLElement | null;
  parentBottom.value = document.querySelector(".BaseToast-container--bottom") as HTMLElement | null;
  if (parentTop.value && parentBottom.value)
    return;

  if (!parentTop.value) {
    const topElement = document.createElement("div");
    topElement.className = "BaseToast-container BaseToast-container--top";
    parentTop.value = topElement;
  }
  if (!parentBottom.value) {
    const bottomElement = document.createElement("div");
    bottomElement.className = "BaseToast-container BaseToast-container--bottom";
    parentBottom.value = bottomElement;
  }
}

function setupContainer() {
  const container = document.body;
  if (parentTop.value && parentBottom.value) {
    container.appendChild(parentTop.value as unknown as Node);
    container.appendChild(parentBottom.value as unknown as Node);
  }
  else {
    console.error("Failed to setup toast containers!");
  }
}

function showNotice() {
  correctParent.value.insertAdjacentElement("afterbegin", rootEl.value);
  isActive.value = true;
  timer.value
    = props.duration !== false ? new Timer(close, props.duration) : null;
}
function toggleTimer(newVal: boolean) {
  if (timer.value && props.pauseOnHover)
    newVal ? timer.value.pause() : timer.value.resume();
}
function stopTimer() {
  timer.value && timer.value.stop();
}
function close() {
  stopTimer();
  isActive.value = false;
  setTimeout(() => {
    props.onClose && props.onClose();
    rootEl.value && removeElement(rootEl.value);
  }, 150);
}

function onClick() {
  props.onClick && props.onClick();

  if (props.dismissible)
    close();
}

function onCustomComponentClick() {
  stopTimer();
  isActive.value = false;
  setTimeout(() => {
    props.onClick && props.onClick();
    rootEl.value && removeElement(rootEl.value);
  }, 150);
}

function onCustomComponentClose() {
  close();
}

function onRootClick() {
  if (!hasCustomComponent.value) {
    onClick();
  }
}

onBeforeMount(() => {
  createParents();
  setupContainer();
});
onMounted(() => showNotice());
</script>

<template>
  <transition
    :enter-active-class="transition.enter"
    :leave-active-class="transition.leave"
  >
    <div
      v-show="isActive"
      ref="rootEl"
      class="BaseToast"
      :class="[
        props.type ? `BaseToast--${props.type}` : '',
        `BaseToast--${props.position}`,
      ]"
      role="alert"
      @mouseover="toggleTimer(true)"
      @mouseleave="toggleTimer(false)"
      @click="onRootClick"
    >
      <component
        :is="props.customComponent"
        v-if="hasCustomComponent"
        v-bind="props.customComponentProps"
        @on-click="onCustomComponentClick"
        @on-close="onCustomComponentClose"
      />
      <BaseAlert
        v-else
        :type="props.type"
        :text="props.message"
        :dismissible="props.dismissible"
      />
    </div>
  </transition>
</template>

<style lang="scss">
@use "./BaseToast.scss";
</style>
